import React, { useState } from 'react';
import SVGImage from '../layout/SVGImage';

enum InputValidationState {
  Guidance,
  Error,
  Success,
}

interface LoginInputProps {
  id: string;
  name: string;
  text: string;
  type: string;
  placeholder: string;
  setValue: (value: string) => void;
  required?: boolean;
  className?: string;
  error?: string;
  guidance?: string;
  success?: string;
  value?: string;
  testId?: string;
}

const LoginInput: React.FC<LoginInputProps> = ({
  id,
  name,
  text,
  type,
  placeholder,
  setValue,
  required,
  className,
  guidance,
  error,
  success,
  value,
  testId,
}) => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const togglePasswordVisibility = () => {
    setIsPasswordVisible((prevState) => !prevState);
  };

  const isVisible = (type: InputValidationState): boolean => {
    switch (type) {
      case InputValidationState.Guidance:
        return !!guidance && !error && !success;
      case InputValidationState.Error:
        return !!error && !success;
      case InputValidationState.Success:
        return !!success;
    }
  };

  const getBorderColorStyle = (): string => {
    if (success) {
      return 'border-teal-main';
    } else if (error) {
      return 'border-red-main';
    } else {
      return 'border-dark-4';
    }
  };

  return (
    <div className={`flex h-[75px] flex-col ${className}`}>
      <label htmlFor={id} className="h-auto leading-none">
        {text}
      </label>
      <div className="relative">
        <input
          data-testid={testId}
          type={isPasswordVisible ? 'text' : type}
          id={id}
          name={name}
          placeholder={placeholder}
          required={!!required}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className={`w-full border-0 border-b pr-10 text-dark-4 ${getBorderColorStyle()} pop-15-normal pb-[8px] pt-[15px] leading-[14px] placeholder:pop-15-normal placeholder:text-light-5 focus:outline-none`}
        />
        {/* Postfix Icon */}
        {type === 'password' && (
          <button
            type="button"
            onClick={togglePasswordVisibility}
            className="absolute right-2 top-1/2 -translate-y-1/2 transform pb-[8px] pt-[15px] text-gray-500"
          >
            {isPasswordVisible ? (
              <SVGImage name={'eye-slash'}></SVGImage>
            ) : (
              <SVGImage name={'eye'}></SVGImage>
            )}
          </button>
        )}
      </div>
      {/* guidance message */}
      {isVisible(InputValidationState.Guidance) && (
        <div className="flex-direction-horizontal flex pt-[4px]">
          <SVGImage name={'error'}></SVGImage>
          <p className="noto-body-small pl-[3px] text-dark-3">{guidance}</p>
        </div>
      )}
      {/* error message */}
      {isVisible(InputValidationState.Error) && (
        <div className="flex-direction-horizontal flex pt-[4px]">
          <SVGImage name={'warning'}></SVGImage>
          <p className="noto-body-small pl-[3px] text-red-main">{error}</p>
        </div>
      )}
      {/* success message */}
      {isVisible(InputValidationState.Success) && (
        <div className="flex-direction-horizontal flex items-center pt-[4px]">
          <SVGImage name={'check'}></SVGImage>
          <p className="noto-body-small pl-[3px] text-teal-main">{success}</p>
        </div>
      )}
    </div>
  );
};

export default LoginInput;
